/* ==========================================================================
   Akmas cart: AJAX drawer + cart page driven by /cart.js
   - Fetches cart via Storefront AJAX API
   - Tier progress bar (1 / 2 / 3+ perfumes)
   - Savings, free handmade soap line, prepaid note
   - Free gift: picks one random in-stock soap from
     window.AkmasOffers.gift.variants, adds it with gift line properties and
     a cart attribute for the packing team, and removes it when the last
     perfume leaves the cart. A soap the shopper adds themselves stays a
     normal line and is never touched.
   Uses Horizon's @shopify/events to react to add-to-cart and auto-open.
   ========================================================================== */
import { StandardEvents } from '@shopify/events';

const CONFIG = () => window.AkmasCartConfig || {};
/* Offer rules (bundles, gift, prepaid %, COD), single source of truth is
   snippets/akmas-offer-config.liquid, emitted as window.AkmasOffers. */
const OFFERS = () => window.AkmasOffers || {};
const norm = (value) => String(value == null ? '' : value).trim();

/* Broadcast every cart change so other components (the Diwali offer ladders
   in assets/theme.js) can redraw from the same payload, no second fetch. */
function emitCartUpdated(cart) {
  try {
    window.dispatchEvent(new CustomEvent('akmas:cart-updated', { detail: cart }));
  } catch (error) {
    window.dispatchEvent(new Event('akmas:cart-updated'));
  }
}

/* GoKwik Cart Drawer & Upsell (the "KwikCart" app embed) owns the cart when its
   slide cart has loaded; it exposes window.openGokwikSideCart as its opener.
   While that is present we route every native "open cart" call to GoKwik and
   never open akmas-cart-drawer, so only one cart ever opens. */
function gokwikCartActive() {
  return typeof window.openGokwikSideCart === 'function';
}

/* -------------------------------------------------------------------------
   Money
   ------------------------------------------------------------------------- */
function formatMoney(cents) {
  const cfg = CONFIG();
  const value = (Number(cents) || 0) / 100;
  try {
    return new Intl.NumberFormat(cfg.locale || 'en-IN', {
      style: 'currency',
      currency: cfg.currency || 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  } catch (error) {
    return '₹' + value.toFixed(0);
  }
}

/* -------------------------------------------------------------------------
   Cart API
   ------------------------------------------------------------------------- */
function endpoints() {
  const routes = CONFIG().routes || {};
  return {
    cart: `${routes.cart || '/cart'}.js`,
    add: `${routes.add || '/cart/add'}.js`,
    change: routes.change || '/cart/change',
    update: routes.update || '/cart/update',
  };
}

async function fetchCart() {
  const response = await fetch(endpoints().cart, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error('Failed to load cart');
  return response.json();
}

async function addItems(items) {
  const response = await fetch(endpoints().add, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ items }),
  });
  if (!response.ok) throw new Error('Failed to add to cart');
  return response.json();
}

async function changeLine(key, quantity) {
  const response = await fetch(endpoints().change, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ id: key, quantity }),
  });
  if (!response.ok) throw new Error('Failed to update cart');
  return response.json();
}

/* Cart attributes survive to the order as "Additional details", which is how
   the packing team sees which soap colour to drop in. Never throws: the gift
   line is already correct on its own, the attribute is only an aid. */
async function updateAttributes(attributes) {
  try {
    const response = await fetch(endpoints().update, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes }),
    });
    if (!response.ok) throw new Error('Failed to update cart attributes');
    return await response.json();
  } catch (error) {
    console.warn('[akmas-cart] cart attribute failed', error);
    return null;
  }
}

/* -------------------------------------------------------------------------
   Free gift: one random in-stock soap per cart
   ------------------------------------------------------------------------- */
const GIFT_STORE_KEY = 'akmas-gift-variant';

function giftConfig() {
  return OFFERS().gift || {};
}

function giftVariants() {
  const variants = giftConfig().variants;
  return Array.isArray(variants) ? variants : [];
}

/* Highest value the gift is worth, for the struck-through price. */
function giftValue() {
  return giftVariants().reduce(
    (best, variant) => Math.max(best, Number(variant.compareAt) || 0, Number(variant.price) || 0),
    0
  );
}

/* Every soap in the gift pool never counts as a perfume: not for the bundle
   tier, not for the savings total, not for the prepaid base. */
function isSoap(item) {
  const id = norm(item.variant_id);
  if (id === '') return false;
  if (giftVariants().some((variant) => norm(variant.id) === id)) return true;
  const single = norm(giftConfig().variantId);
  return single !== '' && id === single;
}

/* Only the line the drawer added itself is auto-managed. A soap the shopper
   added (or bought on its own) is a normal line: real price, normal quantity
   controls, removable, and never silently taken away. The non-underscore
   property is the detector because underscore properties are the ones Shopify
   may hide from a customer-facing payload. */
function isGiftLine(item) {
  if (!isSoap(item)) return false;
  const properties = item.properties || {};
  return norm(properties['_ak_gift']) === '1' || norm(properties['Free gift']) === 'Yes';
}

function perfumeCount(cart) {
  return (cart.items || [])
    .filter((item) => !isSoap(item))
    .reduce((sum, item) => sum + (item.quantity || 0), 0);
}

/* -------------------------------------------------------------------------
   Tier / offer logic
   ------------------------------------------------------------------------- */
function bundleCents(count) {
  const b = OFFERS().bundles || {};
  return {
    one: Number(b[1]) || 0,
    two: Number(b[2]) || 0,
    three: Number(b[3]) || 0,
  };
}

/* Bundle price for ANY mix of N perfumes.
   Mirrors the Akmas pack discount Function: try every mix of 2-packs and
   3-packs that covers at most N perfumes and pick the cheapest total,
   pricing any remainder at the single-perfume price. With uniform prices:
     4 → 2×2-pack ₹1,998  ·  5 → 2-pack + 3-pack  ·  6 → 2×3-pack ₹2,598 …
   The real discount is enforced server-side; this is display-only. */
function bundlePriceCents(count) {
  const { one, two, three } = bundleCents();
  if (count <= 0) return 0;
  let best = count * one;
  for (let threes = 0; threes <= Math.floor(count / 3); threes += 1) {
    for (let twos = 0; twos <= Math.floor((count - threes * 3) / 2); twos += 1) {
      const covered = threes * 3 + twos * 2;
      const total = threes * three + twos * two + (count - covered) * one;
      if (total < best) best = total;
    }
  }
  return best;
}

function tierState(cart) {
  const { two, three } = bundleCents();
  const count = perfumeCount(cart);
  const price2 = formatMoney(two);
  const price3 = formatMoney(three);

  let stage = 0;
  let message = '';
  if (count <= 0) {
    stage = 0;
    message = `Add 2 for ${price2} or 3 for ${price3}`;
  } else if (count === 1) {
    stage = 1;
    message = `Add 1 more for ${price2} total.`;
  } else if (count === 2) {
    stage = 2;
    message = `Add 1 more for ${price3} total.`;
  } else {
    stage = 3;
    message = "You've unlocked the best bundle price.";
  }

  return {
    count,
    stage,
    message,
    progress: Math.min(count, 3) / 3,
  };
}

/* -------------------------------------------------------------------------
   Savings + prepaid
   ------------------------------------------------------------------------- */
function computeSavings(cart) {
  let baseline = 0;
  (cart.items || []).forEach((item) => {
    if (isSoap(item)) return;
    const unit = Math.max(item.compare_at_price || 0, item.original_price || 0, item.price || 0);
    baseline += unit * (item.quantity || 0);
  });
  const count = perfumeCount(cart);
  return Math.max(0, baseline - bundlePriceCents(count));
}

function prepaidAmount(cart) {
  const percent = Number(OFFERS().prepaidPercent) || 0;
  const count = perfumeCount(cart);
  const base = count > 0 ? bundlePriceCents(count) : (cart.total_price || 0);
  return Math.round((base * percent) / 100);
}

/* -------------------------------------------------------------------------
   Free-gift sync: add one random in-stock soap while a perfume is in the
   cart, take it away again when the last perfume goes. Runs from the drawer,
   from the cart page, after every add-to-cart, and once when a shopping page
   loads, so the gift is there even if the shopper never opens the drawer.
   ------------------------------------------------------------------------- */
let syncing = false;
const GIFT_BLOCKED_KEY = 'akmas-gift-blocked';
const GIFT_RETRY_MS = 60 * 60 * 1000;

/* The store has not configured the server-side rule yet (see the free-gift
   checklist): trying again on every single refresh would just add and remove
   a line over and over, so the miss is remembered for an hour. */
function giftBlocked() {
  try {
    const at = Number(window.sessionStorage.getItem(GIFT_BLOCKED_KEY)) || 0;
    return at > 0 && Date.now() - at < GIFT_RETRY_MS;
  } catch (error) {
    return false;
  }
}

function setGiftBlocked(blocked) {
  try {
    if (blocked) window.sessionStorage.setItem(GIFT_BLOCKED_KEY, String(Date.now()));
    else window.sessionStorage.removeItem(GIFT_BLOCKED_KEY);
  } catch (error) {
    /* storage unavailable: worst case is one extra add/remove attempt */
  }
}

/* A gift that still costs money after discounts is not a gift. If the
   server-side rule is missing, the line is taken straight back out instead of
   quietly charging the customer for it. */
function giftIsFree(item) {
  const paid = Number(item.final_line_price) || 0;
  const allocations = item.discount_allocations || item.line_level_discount_allocations || [];
  const covered = allocations.reduce(
    (sum, allocation) => sum + (Number(allocation.amount) || 0),
    0
  );
  return paid - covered <= 0;
}

function secureIndex(max) {
  if (max <= 1) return 0;
  try {
    const values = new Uint32Array(1);
    window.crypto.getRandomValues(values);
    return values[0] % max;
  } catch (error) {
    return Math.floor(Math.random() * max);
  }
}

/* One random in-stock soap that is not already in the cart. The pick is
   remembered for the session, so a second order in the same session gets the
   same colour instead of a surprise. Returns null when nothing is in stock:
   no gift, no message, no half-added line. */
function pickGift(cart) {
  const inCart = new Set(
    (cart.items || []).filter(isSoap).map((item) => norm(item.variant_id))
  );
  const pool = giftVariants().filter(
    (variant) => variant.available && !inCart.has(norm(variant.id))
  );
  if (pool.length === 0) return null;

  let stored = '';
  try {
    stored = window.sessionStorage.getItem(GIFT_STORE_KEY) || '';
  } catch (error) {
    stored = '';
  }
  const remembered = pool.find((variant) => norm(variant.id) === stored);
  if (remembered) return remembered;

  const pick = pool[secureIndex(pool.length)];
  try {
    window.sessionStorage.setItem(GIFT_STORE_KEY, norm(pick.id));
  } catch (error) {
    /* private mode: the pick is still made, just not remembered */
  }
  return pick;
}

async function syncSoap(cart) {
  if (syncing) return cart;

  const items = cart.items || [];
  const giftLines = items.filter(isGiftLine);
  const count = perfumeCount(cart);

  if (count > 0 && giftLines.length === 0) {
    if (giftBlocked()) return cart;
    const gift = pickGift(cart);
    if (!gift) return cart;
    syncing = true;
    try {
      await addItems([
        {
          id: gift.id,
          quantity: 1,
          properties: {
            _ak_gift: '1',
            'Free gift': 'Yes',
            Colour: norm(gift.colour),
          },
        },
      ]);
      let updated = await fetchCart();
      const added = (updated.items || []).find(isGiftLine);
      if (added && !giftIsFree(added)) {
        await changeLine(added.key, 0);
        setGiftBlocked(true);
        console.warn(
          '[akmas-cart] the free soap is not discounted server-side yet, so it was removed again. Set up the free-gift discount, then reload.'
        );
        updated = await fetchCart();
        return updated;
      }
      setGiftBlocked(false);
      await updateAttributes({ 'Free gift colour': norm(gift.colour) });
      return updated;
    } catch (error) {
      console.warn('[akmas-cart] gift add failed', error);
      return cart;
    } finally {
      syncing = false;
    }
  }

  if (count === 0 && giftLines.length > 0) {
    syncing = true;
    try {
      for (const line of giftLines) {
        await changeLine(line.key, 0);
      }
      try {
        window.sessionStorage.removeItem(GIFT_STORE_KEY);
      } catch (error) {
        /* storage unavailable, nothing to forget */
      }
      const updated = await fetchCart();
      await updateAttributes({ 'Free gift colour': '' });
      return updated;
    } catch (error) {
      console.warn('[akmas-cart] gift remove failed', error);
      return cart;
    } finally {
      syncing = false;
    }
  }

  return cart;
}

/* Cart-level enforcement that does not need an open drawer. */
let backgroundSyncTimer = null;
async function backgroundSync() {
  if (syncing) return;
  try {
    const cart = await fetchCart();
    const updated = await syncSoap(cart);
    if (updated === cart) return;
    const drawer = activeDrawer();
    if (drawer && !drawer.hasAttribute('hidden')) drawer.refresh();
    document.querySelectorAll('akmas-cart-page').forEach((page) => page.refresh());
  } catch (error) {
    console.warn('[akmas-cart] background gift sync failed', error);
  }
}
function scheduleGiftSync() {
  window.clearTimeout(backgroundSyncTimer);
  backgroundSyncTimer = window.setTimeout(backgroundSync, 400);
}

/* -------------------------------------------------------------------------
   Rendering
   ------------------------------------------------------------------------- */
function escapeHtml(value) {
  return String(value == null ? '' : value).replace(/[&<>"']/g, (char) => {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char];
  });
}

function lineItemHtml(item) {
  const soap = isSoap(item);
  const gift = isGiftLine(item);
  const image = item.image
    ? `<img class="akc-line__img" src="${item.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2')}" alt="${escapeHtml(item.product_title)}" width="80" height="80" loading="lazy">`
    : '';
  const variant = item.variant_title && item.variant_title !== 'Default Title'
    ? `<span class="akc-line__variant">${escapeHtml(item.variant_title)}</span>`
    : '';

  /* The gift is shown at its real value struck through plus a "Free gift"
     label, never a bare FREE: the same soap is also a real product with a
     real price, and the line must read as a gift, not as a discount bug. */
  const giftWorth = giftValue();
  const priceHtml = gift
    ? `<span class="akc-line__price akc-line__price--gift">` +
      `${giftWorth > 0 ? `<s>${formatMoney(item.compare_at_price || giftWorth)}</s>` : ''}` +
      `<span>Free gift</span></span>`
    : `<span class="akc-line__price">${formatMoney(item.final_line_price)}</span>`;

  const qtyHtml = gift
    ? `<span class="akc-line__qty-static">Qty 1</span>`
    : `<div class="akc-qty" data-line="${escapeHtml(item.key)}">
         <button type="button" class="akc-qty__btn" data-akc-change="-1" aria-label="Decrease quantity">−</button>
         <input class="akc-qty__input" type="text" inputmode="numeric" value="${item.quantity}" data-akc-qty-input aria-label="Quantity" readonly>
         <button type="button" class="akc-qty__btn" data-akc-change="1" aria-label="Increase quantity">+</button>
       </div>`;

  const removeHtml = gift
    ? ''
    : `<button type="button" class="akc-line__remove" data-akc-remove aria-label="Remove ${escapeHtml(item.product_title)}">Remove</button>`;

  return `
    <li class="akc-line${soap ? ' akc-line--soap' : ''}${gift ? ' akc-line--gift' : ''}" data-key="${escapeHtml(item.key)}">
      <div class="akc-line__media">${image}</div>
      <div class="akc-line__body">
        <a class="akc-line__title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
        ${variant}
        <div class="akc-line__controls">
          ${qtyHtml}
          ${priceHtml}
        </div>
        ${removeHtml}
      </div>
    </li>`;
}

/* The cart drawer shows the compact variant of the shared Diwali offer ladder
   (snippets/akmas-offer-ladder.liquid). Same markup, same data attributes, so
   assets/theme.js drives the live line and the reached-step ticks for the
   drawer exactly as it does on the home page and category pages. */
function tierBarHtml(cart) {
  const { one, two, three } = bundleCents();
  const stage = Math.max(1, Math.min(perfumeCount(cart), 3));
  const check =
    '<span class="ak-ladder__check">' +
    '<svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" ' +
    'stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="m3 8.5 3.2 3.2L13 4.4"/></svg></span>';
  const node = '<span class="ak-ladder__node" aria-hidden="true">' + check + '</span>';
  const step = (n) =>
    `<li class="ak-ladder__step" data-ak-ladder-step="${n}">${node}` +
    `<span class="ak-ladder__mini-label">${n}</span></li>`;

  return `
    <section class="ak-ladder ak-ladder--compact" data-ak-ladder
      data-ak-offer-place="cart"
      data-ak-ladder-prices="${one},${two},${three}"
      data-ak-stage="${stage}"
      aria-label="Diwali offer progress">
      <p class="ak-ladder__mini-line" data-ak-ladder-line>Pick 3 perfumes and save ${formatMoney(one * 3 - three)}</p>
      <ol class="ak-ladder__steps ak-ladder__steps--mini">
        ${step(1)}
        ${step(2)}
        ${step(3)}
      </ol>
      <p class="ak-sr-only" data-ak-ladder-status>Step ${stage} of 3 reached</p>
    </section>`;
}

function summaryHtml(cart) {
  const cfg = CONFIG();
  const offers = OFFERS();
  const count = perfumeCount(cart);
  const bundleTotal = count > 0 ? bundlePriceCents(count) : (cart.total_price || 0);
  /* When the Akmas pack discount Function is live, /cart.js carries the real
     server-side discount, prefer it over the client-side estimate so the
     drawer, cart page and checkout always agree. */
  const serverDiscount = (cart.cart_level_discount_applications || []).reduce(
    (sum, app) => sum + (Number(app.total_amount) || 0),
    0
  );
  const hasServerDiscount = serverDiscount > 0 && count > 0;
  const payable = hasServerDiscount ? (cart.total_price || 0) : bundleTotal;
  const savings = computeSavings(cart);
  const prepaidPercent = Number(offers.prepaidPercent) || 0;
  const prepaid = Math.round((payable * prepaidPercent) / 100);
  const codFee = Number(offers.codFee) || 0;
  const gift = offers.gift || {};
  const giftTitle = gift.phrase || gift.title || 'Free handmade soap';
  const hasGiftLine = (cart.items || []).some(isGiftLine);
  const countLabel = count === 1 ? '1 perfume' : `${count} perfumes`;

  const savingsHtml = savings > 0
    ? `<div class="akc-summary__row akc-summary__row--save"><span>You save</span><span>${formatMoney(savings)}</span></div>`
    : '';

  const bundleHtml = hasServerDiscount
    ? `<div class="akc-summary__row akc-summary__row--save"><span>Diwali bundle discount</span><span>&minus;${formatMoney(serverDiscount)}</span></div>
       <div class="akc-summary__row akc-summary__row--total"><span>Total (${escapeHtml(countLabel)})</span><span>${formatMoney(payable)}</span></div>`
    : count > 0
      ? `<div class="akc-summary__row akc-summary__row--total"><span>Bundle price (${escapeHtml(countLabel)})</span><span>${formatMoney(bundleTotal)}</span></div>`
      : `<div class="akc-summary__row akc-summary__row--total"><span>Subtotal</span><span>${formatMoney(bundleTotal)}</span></div>`;

  /* Only ever claim the gift once it is really in the cart and really free. */
  const giftHtml = hasGiftLine
    ? `<div class="akc-summary__row akc-summary__row--gift"><span>${escapeHtml(giftTitle)}</span><span>FREE</span></div>`
    : '';

  const payHtml = count > 0
    ? `<div class="akc-summary__pay" role="group" aria-label="Payment options">
         <div class="akc-summary__pay-row">
           <span>Prepaid discount (${prepaidPercent}%)</span>
           <span>&minus;${formatMoney(prepaid)}</span>
         </div>
         <div class="akc-summary__pay-total">
           <span>Pay online</span><span>${formatMoney(Math.max(0, payable - prepaid))}</span>
         </div>
         <div class="akc-summary__pay-row akc-summary__pay-row--cod">
           <span>Cash on delivery fee</span>
           <span>+${formatMoney(codFee)}</span>
         </div>
         <div class="akc-summary__pay-total">
           <span>COD total</span><span>${formatMoney(payable + codFee)}</span>
         </div>
       </div>`
    : '';

  return `
    ${savingsHtml}
    ${bundleHtml}
    ${giftHtml}
    ${payHtml}
    <a class="ak-btn ak-btn--primary akc-summary__checkout" href="${escapeHtml((cfg.routes && cfg.routes.checkout) || '/checkout')}">Checkout</a>
    <a class="akc-summary__view" href="${escapeHtml((cfg.routes && cfg.routes.cart) || '/cart')}">View cart</a>`;
}

function renderCart(container, cart) {
  const items = cart.items || [];

  if (items.length === 0) {
    container.innerHTML = `
      <div class="akc-empty">
        <p>Your cart is empty.</p>
        <a class="ak-btn ak-btn--primary" href="${escapeHtml((CONFIG().routes && CONFIG().routes.all) || '/collections/all')}">Start shopping</a>
      </div>`;
    container.classList.add('is-empty');
    return;
  }

  container.classList.remove('is-empty');
  container.innerHTML = `
    <div class="akc-tier-wrap">${tierBarHtml(cart)}</div>
    <ul class="akc-lines">${items.map(lineItemHtml).join('')}</ul>
    <div class="akc-summary">${summaryHtml(cart)}</div>`;
}

/* -------------------------------------------------------------------------
   Base element with shared behaviour
   ------------------------------------------------------------------------- */
class AkmasCartBase extends HTMLElement {
  #container = null;
  #busy = false;

  connectedCallback() {
    this.#container = this.querySelector('[data-akc-container]') || this;
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  get container() {
    return this.#container || this;
  }

  async refresh() {
    if (this.#busy) return;
    this.#busy = true;
    this.container.classList.add('akc-loading');
    try {
      let cart = await fetchCart();
      cart = await syncSoap(cart);
      this.currentCart = cart;
      renderCart(this.container, cart);
      emitCartUpdated(cart);
    } catch (error) {
      console.warn('[akmas-cart] refresh failed', error);
    } finally {
      this.container.classList.remove('akc-loading');
      this.#busy = false;
    }
  }

  #onClick = async (event) => {
    const changeBtn = event.target.closest('[data-akc-change]');
    const removeBtn = event.target.closest('[data-akc-remove]');
    if (!changeBtn && !removeBtn) return;
    event.preventDefault();

    const line = event.target.closest('[data-key]');
    if (!line) return;
    const key = line.getAttribute('data-key');
    const item = (this.currentCart?.items || []).find((entry) => entry.key === key);
    if (!item) return;

    let quantity = item.quantity;
    if (removeBtn) {
      quantity = 0;
    } else if (changeBtn) {
      quantity = Math.max(0, item.quantity + Number(changeBtn.getAttribute('data-akc-change')));
    }

    line.classList.add('is-busy');
    try {
      await changeLine(key, quantity);
      await this.refresh();
    } catch (error) {
      line.classList.remove('is-busy');
    }
  };
}

/* -------------------------------------------------------------------------
   Drawer
   ------------------------------------------------------------------------- */
class AkmasCartDrawer extends AkmasCartBase {
  #lastFocused = null;

  get panel() {
    return this.querySelector('[data-akc-panel]');
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  open(trigger) {
    if (gokwikCartActive()) {
      try {
        window.openGokwikSideCart();
      } catch (error) {
        console.warn('[akmas-cart] GoKwik cart open failed', error);
      }
      return;
    }
    this.#lastFocused = trigger || document.activeElement;
    this.removeAttribute('hidden');
    document.body.classList.add('ak-no-scroll');
    requestAnimationFrame(() => requestAnimationFrame(() => this.setAttribute('open', '')));
    const close = this.querySelector('[data-akc-close]');
    if (close) close.focus();
    this.refresh();
  }

  close() {
    this.removeAttribute('open');
    document.body.classList.remove('ak-no-scroll');
    const finish = () => {
      this.setAttribute('hidden', '');
      this.removeEventListener('transitionend', finish);
    };
    this.addEventListener('transitionend', finish);
    setTimeout(finish, 320);
    if (this.#lastFocused && typeof this.#lastFocused.focus === 'function') {
      this.#lastFocused.focus();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.querySelectorAll('[data-akc-close]').forEach((el) => {
      el.addEventListener('click', () => this.close());
    });
    document.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.#onKeydown);
  }

  #onKeydown = (event) => {
    if (event.key === 'Escape' && this.isOpen) this.close();
  };
}

if (!customElements.get('akmas-cart-drawer')) {
  customElements.define('akmas-cart-drawer', AkmasCartDrawer);
}

/* -------------------------------------------------------------------------
   Cart page
   ------------------------------------------------------------------------- */
class AkmasCartPage extends AkmasCartBase {
  connectedCallback() {
    super.connectedCallback();
    this.refresh();
  }
}

if (!customElements.get('akmas-cart-page')) {
  customElements.define('akmas-cart-page', AkmasCartPage);
}

/* -------------------------------------------------------------------------
   Global wiring
   ------------------------------------------------------------------------- */
function activeDrawer() {
  return document.querySelector('akmas-cart-drawer');
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-ak-cart-open]');
  if (!trigger) return;
  const drawer = activeDrawer();
  if (!drawer) return;
  event.preventDefault();
  drawer.open(trigger);
});

// Auto-open on add-to-cart; refresh both drawer and cart page on any change.
// The gift sync also runs here in the background, so a free soap is still
// added when another cart app (GoKwik) owns the drawer and no native refresh
// would otherwise happen.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const drawer = activeDrawer();
  if (drawer && event && event.action === 'add' && !gokwikCartActive()) drawer.open();
  else if (drawer) drawer.refresh();
  document.querySelectorAll('akmas-cart-page').forEach((page) => page.refresh());
  scheduleGiftSync();
  emitCartUpdated(null);
});

// One catch-up pass when a page that can hold a cart is shown: a perfume
// added on a page without our drawer (or before a discount was configured)
// still gets its gift before the shopper reaches checkout.
window.addEventListener('load', () => {
  const shoppingPage = document.querySelector(
    '[data-ak-add], [data-akp-form], [data-ak-cart-open], akmas-cart-page'
  );
  if (shoppingPage) scheduleGiftSync();
});

window.AkmasCart = {
  fetchCart,
  addItems,
  changeLine,
  formatMoney,
  tierState,
  gokwikCartActive,
  isSoap,
  isGiftLine,
  giftVariants,
};
